/**
 * The console's identity read — task 27 (console integration). Tasks 13 and
 * 15 both flagged the same gap: nothing in the store or config carries the
 * signed-in staff member's own id/name, so the initials menu showed the
 * literal "You"/"Me" and every ownership comparison
 * (`components/thread/format.ts`'s `isOwnedByMe`/`isOwnedBySomeoneElse`) used
 * a fixed `'me'` sentinel lifted from the design prototype's seed-data
 * convention. That sentinel can never match a real `staff.id` (a UUID) once
 * this console talks to the actual `services/ops` backend — a dispatcher who
 * claims a thread would see it as "owned by someone else" forever after,
 * because the string `'me'` never equals a real id. That is the concrete bug
 * this file exists to close off.
 *
 * There is, however, genuinely no whoami-shaped read anywhere in
 * `services/ops` yet (confirmed by reading `services/ops/src/routes/reads.ts`
 * and `services/ops/src/actor.ts` — actor resolution already computes exactly
 * this per request for gating, `{ id, role, admin }` off the `staff` table,
 * it just isn't exposed as a read a browser can call), and no staff sign-in
 * screen exists anywhere in this 26-task build (only backend routes,
 * `POST /auth/signin/staff` + `/verify`, in services/auth) — enrolment/auth
 * UI is out of Phase 1's scope per every prior task's own notes. Building a
 * server route is also out of this task's file scope (`apps/console/` only;
 * `services/ops` is other agents' territory this session).
 *
 * So this follows the exact seam pattern already established twice in this
 * codebase (`components/context/aiOps.ts` for the AI tab,
 * `components/modals/reads.ts` for the four modal reads): a real, injectable
 * network attempt against the natural extension of the actor model that
 * already exists server-side, with an honest `null` fallback — never a
 * fabricated identity — on any failure. Today that fallback fires always,
 * since the route doesn't exist; the moment `services/ops` grows a genuine
 * `GET /ops/whoami`, every consumer of `identity.whoAmI()` starts reflecting
 * the real signed-in person with zero component changes.
 *
 * Task 31 closed the gap this file was waiting on: `GET /ops/whoami`
 * (services/ops/src/routes/whoami.ts) is real now, and `email`/`covering`
 * were added below because the wire response actually carries them (they
 * come straight off the `staff` table, same as `id`/`name`/`role`/`admin`).
 * Both are optional rather than required — every existing caller built
 * against the four-field shape (the initials menu, ownership comparisons)
 * keeps working untouched, and a body that only carries the original four
 * fields is still a valid, real identity, not a malformed one.
 *
 * `superAdmin` joins them the same optional way, added when
 * `GET /ops/whoami` grew the field (services/ops/src/routes/whoami.ts,
 * users-and-permissions design §6) — the Users page needs to know whether
 * the viewer IS the SuperAdmin to decide whether to offer its own
 * grant-admin control at all, and there was nowhere the console read that
 * about itself before now.
 */
import type { StaffRole } from '@communicator/shared'
import { loadRuntimeConfig } from '../runtime-config'

export interface StaffIdentity {
  id: string
  name: string
  role: StaffRole
  admin: boolean
  email?: string
  covering?: boolean
  superAdmin?: boolean
  /**
   * The 3CX extension this person sits at, set by an admin in Settings ->
   * Users. Null or absent when nobody has set one, which is the honest
   * default: the live-call bar then names whoever holds the call rather than
   * claiming it is yours.
   */
  ext?: string | null
}

interface IdentityClientDeps {
  /** Defaults to the global `fetch`. Override in tests. */
  fetchImpl?: typeof fetch
  /** Defaults to reading /runtime-config.json via loadRuntimeConfig(). Mirrors ../../lib/ops.ts's own deps shape. */
  baseUrl?: string | (() => string | Promise<string>)
}

function isStaffIdentityShape(body: unknown): body is StaffIdentity {
  if (typeof body !== 'object' || body === null) return false
  const b = body as Record<string, unknown>
  return (
    typeof b.id === 'string' &&
    b.id.length > 0 &&
    typeof b.name === 'string' &&
    (b.role === 'dispatch' || b.role === 'tech') &&
    typeof b.admin === 'boolean' &&
    // Optional fields: present or absent is both valid, but if present must
    // be the right type — never silently accept a malformed extra field.
    (b.email === undefined || typeof b.email === 'string') &&
    (b.covering === undefined || typeof b.covering === 'boolean') &&
    (b.superAdmin === undefined || typeof b.superAdmin === 'boolean')
  )
}

/**
 * Builds the identity read client. `whoAmI()` always resolves — it never
 * throws — because a missing route, a network failure and "not signed in"
 * all mean the same thing to every caller: identity is not established, show
 * the honest fallback, never a guessed name.
 */
export function createIdentityClient(deps: IdentityClientDeps = {}) {
  const doFetch: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args))

  async function resolveBase(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  /**
   * Who is signed in, or why we cannot say.
   *
   * THREE ANSWERS, NOT TWO. This used to return null for every non-2xx and
   * every throw, and bootstrap turns null into `signed-out` — so a 502 while a
   * container restarted showed the sign-in page to someone whose session was
   * perfectly valid. Andrew hit this repeatedly: "I just get the sign in page
   * and force refresh" and he is back in, because the cookies were fine all
   * along and the server had simply come back.
   *
   * Same distinction `sessionRefresh.ts` already makes. It was fixed there and
   * not here, so the symptom moved rather than went away.
   */
  async function whoAmI(): Promise<StaffIdentity | null | 'unreachable'> {
    try {
      const base = await resolveBase()
      const res = await doFetch(`${base}/ops/whoami`, { credentials: 'include' })
      // 401/403 is the server saying nobody is signed in. That is an answer.
      if (res.status === 401 || res.status === 403) return null
      // Anything else — 502/503 mid-deploy, a gateway timeout, a 500 — is the
      // server being unable to answer, which says nothing about the session.
      if (!res.ok) return 'unreachable'
      const body: unknown = await res.json().catch(() => null)
      return isStaffIdentityShape(body) ? body : null
    } catch {
      // Network failure. Also not an answer about the session.
      return 'unreachable'
    }
  }

  return { whoAmI }
}

/** The singleton every real caller (the console's bootstrap) uses — same pattern as `ops` in ./ops.ts. */
export const identity = createIdentityClient()
